import {
  Box,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import CreativeBlockAdPreviews from "../../components/CreativeBlockAdPreviews";
import type { CreativeBlock } from "../types";
import type { FacebookPage, InstagramAccount } from "../../types";
import {
  hasInstagramAccount,
  hasSquareCreativeSlot,
  hasVerticalCreativeSlot,
} from "../creativePreviewSlots";

type CreateCampaignFlowPreviewProps = {
  mobilePanel: "editor" | "preview";
  creatives: CreativeBlock[];
  fieldIds: string[];
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  ctaText: string;
  websiteUrl: string;
  eventName: string;
  eventDescription: string;
  partnerPostsEnabled?: boolean;
};

export default function CreateCampaignFlowPreview({
  mobilePanel,
  creatives,
  fieldIds,
  pages,
  instagramAccounts,
  ctaText,
  websiteUrl,
  eventName,
  eventDescription,
  partnerPostsEnabled = false,
}: CreateCampaignFlowPreviewProps) {
  const hasInstagramPreview = useMemo(
    () =>
      creatives.some(
        (c) =>
          hasInstagramAccount(c) &&
          (hasSquareCreativeSlot(c) || hasVerticalCreativeSlot(c))
      ),
    [creatives]
  );

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    setTabIndex(hasInstagramPreview ? 1 : 0);
  }, [hasInstagramPreview]);

  const instagramPreviews = (
    <VStack align="stretch" spacing={6} w="full">
      {fieldIds.map((fieldId, index) => {
        const creative = creatives[index];
        if (!creative) return null;
        const showSquare = hasSquareCreativeSlot(creative);
        const showVertical = hasVerticalCreativeSlot(creative);
        const hasInstagram = hasInstagramAccount(creative);
        if (!hasInstagram || (!showSquare && !showVertical)) return null;

        return (
          <CreativeBlockAdPreviews
            key={`instagram-preview-${fieldId}`}
            creative={creative}
            pages={pages}
            instagramAccounts={instagramAccounts}
            ctaText={ctaText}
            websiteUrl={websiteUrl}
            eventName={eventName}
            eventDescription={eventDescription}
            platforms={["instagram"]}
            partnerPostsEnabled={partnerPostsEnabled}
          />
        );
      })}
    </VStack>
  );

  const facebookPreviews = (
    <VStack align="stretch" spacing={6} w="full">
      {fieldIds.map((fieldId, index) => {
        const creative = creatives[index];
        if (!creative) return null;
        const showSquare = hasSquareCreativeSlot(creative);
        const showVertical = hasVerticalCreativeSlot(creative);
        if (!showSquare && !showVertical) return null;

        return (
          <Stack key={`facebook-preview-${fieldId}`} spacing={6}>
            <CreativeBlockAdPreviews
              creative={creative}
              pages={pages}
              instagramAccounts={instagramAccounts}
              ctaText={ctaText}
              websiteUrl={websiteUrl}
              eventName={eventName}
              eventDescription={eventDescription}
              platforms={["facebook"]}
              partnerPostsEnabled={partnerPostsEnabled}
            />
          </Stack>
        );
      })}
    </VStack>
  );

  const instagramNeedsAccountMessage = (
    <Text fontSize="sm" color="gray.600" textAlign="center" px={2} py={4}>
      Select an Instagram account in the creative editor to preview Instagram
      placements.
    </Text>
  );

  return (
    <Box
      flex={1}
      rounded="xl"
      alignSelf="stretch"
      display={{
        base: mobilePanel === "preview" ? "block" : "none",
        md: "block",
      }}
    >
      <Tabs
        variant="soft-rounded"
        colorScheme="gray"
        size="sm"
        index={tabIndex}
        onChange={setTabIndex}
      >
        <TabList bg="gray.200" rounded="lg" p={1} maxW="80%" mx="auto">
          <Tab
            _selected={{ bg: "white", shadow: "sm" }}
            rounded="md"
            width="50%"
          >
            <Text fontSize="xs">Facebook Preview</Text>
          </Tab>
          <Tab
            _selected={{ bg: "white", shadow: "sm" }}
            rounded="md"
            width="50%"
          >
            <Text fontSize="xs">Instagram Preview</Text>
          </Tab>
        </TabList>
        <TabPanels pt={4}>
          <TabPanel px={0}>{facebookPreviews}</TabPanel>
          <TabPanel px={0}>
            {hasInstagramPreview
              ? instagramPreviews
              : instagramNeedsAccountMessage}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
